'use client';

import { createClient } from '@/lib/supabase/client';

export type NotificationType =
  | 'application_status' |'application_received' |'review_approved' |'review_rejected' |'new_review' |'system';

export interface Notification {
  id: string;
  userId: string;
  type: NotificationType;
  title: string;
  message: string;
  isRead: boolean;
  link?: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
}

function mapRow(row: any): Notification {
  return {
    id: row.id,
    userId: row.user_id,
    type: row.type as NotificationType,
    title: row.title,
    message: row.message,
    isRead: row.is_read,
    link: row.link || '',
    metadata: row.metadata || {},
    createdAt: row.created_at,
  };
}

export async function fetchNotifications(userId: string): Promise<Notification[]> {
  const supabase = createClient();
  try {
    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(50);
    if (error) { console.error('fetchNotifications error:', error.message); return []; }
    return (data || []).map(mapRow);
  } catch (err: any) {
    console.error('fetchNotifications exception:', err.message);
    return [];
  }
}

export async function markNotificationRead(id: string): Promise<boolean> {
  const supabase = createClient();
  try {
    const { error } = await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('id', id);
    if (error) { console.error('markNotificationRead error:', error.message); return false; }
    return true;
  } catch (err: any) {
    console.error('markNotificationRead exception:', err.message);
    return false;
  }
}

export async function markAllNotificationsRead(userId: string): Promise<boolean> {
  const supabase = createClient();
  try {
    const { error } = await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('user_id', userId)
      .eq('is_read', false);
    if (error) { console.error('markAllNotificationsRead error:', error.message); return false; }
    return true;
  } catch (err: any) {
    console.error('markAllNotificationsRead exception:', err.message);
    return false;
  }
}

export async function createNotification(
  userId: string,
  type: NotificationType,
  title: string,
  message: string,
  link?: string,
  metadata?: Record<string, unknown>
): Promise<Notification | null> {
  const supabase = createClient();
  try {
    const { data, error } = await supabase
      .from('notifications')
      .insert({ user_id: userId, type, title, message, link: link || '', metadata: metadata || {} })
      .select()
      .single();
    if (error) { console.error('createNotification error:', error.message); return null; }
    return mapRow(data);
  } catch (err: any) {
    console.error('createNotification exception:', err.message);
    return null;
  }
}

export function subscribeToNotifications(
  userId: string,
  onNew: (notification: Notification) => void
) {
  const supabase = createClient();
  const channel = supabase
    .channel(`notifications:${userId}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${userId}`,
      },
      (payload) => {
        if (payload.new) onNew(mapRow(payload.new));
      }
    )
    .subscribe();
  return () => { supabase.removeChannel(channel); };
}
